import React, { useState } from 'react';
import {
  Form, Button, Radio, message, Typography,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { StyledRangePicker } from '../../../../components/Styled/Custom';
import { DEFAULT_DATE_TIME, DEFAULT_TIME } from '../../../../utils/constants/timeFormats';
import { getSelectedCompany, isFeatureEnabled } from '../../../../store/selectors';
import { generateMonitoringEtaCustomerReport } from '../../../../store/actions/reports';
import { FeatureToggle } from '../../../../store/actions';

const { Title } = Typography;
const WEEK_MINUTES = 7 * 24 * 60;

const SectionContainer = styled.div`
  margin-bottom: 24px;
`;

const FormItem = styled(Form.Item)`
  margin-bottom: 16px;
`;

const EtaReportSection = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const selectedCompany = useSelector(getSelectedCompany);
  const etaReportsEnabled = useSelector(isFeatureEnabled(FeatureToggle.ETA_REPORTS_ENABLED));

  const disabledDate = (current) => {
    if (!current) {
      return false;
    }

    // block future dates
    const today = moment();
    if (current.isAfter(today, 'day')) {
      return true;
    }

    // Case 1: Only the start date is selected, limit the selection of the end date
    if (dateRange && dateRange[0] && !dateRange[1]) {
      const startDate = dateRange[0].clone();
      const maxDate = startDate.clone().add(7, 'days');
      const minDate = startDate.clone().subtract(0, 'days');
      return current.isAfter(maxDate) || current.isBefore(minDate);
    }

    // Case 2: Only the end date is selected, limit the selection of the start date
    if (dateRange && !dateRange[0] && dateRange[1]) {
      const endDate = dateRange[1].clone();
      const minDate = endDate.clone().subtract(7, 'days');
      const maxDate = endDate.clone().add(0, 'days');
      return current.isAfter(maxDate) || current.isBefore(minDate);
    }

    return false;
  };

  const isDateRangeExceedingWeek = () => {
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      const dateRangeInMinutes = dateRange[1].diff(dateRange[0], 'minutes');
      return dateRangeInMinutes > WEEK_MINUTES;
    }
    return false;
  };

  const isDownloadDisabled = !etaReportsEnabled
        || form.getFieldsError().some(({ errors }) => errors.length > 0)
        || !dateRange
        || dateRange.length < 2
        || !dateRange[0]
        || !dateRange[1]
        || isDateRangeExceedingWeek();

  const timeRangeValidator = () => {
    const startDate = form.getFieldValue('dateRange')[0];
    const endDate = form.getFieldValue('dateRange')[1];
    if (startDate && endDate) {
      const dateRangeInMinutes = endDate.diff(startDate, 'minutes');
      if (dateRangeInMinutes > WEEK_MINUTES) {
        return Promise.reject();
      }
    }
    return Promise.resolve();
  };

  const handleGenerateReport = () => {
    form.validateFields()
      .then((values) => {
        setLoading(true);
        const { dateRange: range } = values;

        if (!selectedCompany) {
          message.error('No company selected. Please select a company first.');
          setLoading(false);
          return;
        }

        const requestData = {
          shipperCompanyId: selectedCompany,
          contextCompanyId: selectedCompany,
          dateFrom: range[0].unix(),
          dateTo: range[1].unix(),
        };

        dispatch(generateMonitoringEtaCustomerReport(requestData))
          .then(() => {
            message.success(t('REPORTS.ETA_REPORT_GENERATED_SUCCESS'));
            form.resetFields();
            setDateRange([]);
          })
          .catch(() => {
            message.error(t('REPORTS.ETA_REPORT_GENERATED_ERROR'));
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => {
        message.error(t('REPORTS.ETA_REPORT_GENERATED_ERROR'));
      });
  };

  return (
    <SectionContainer>
      <Title level={3}>{t('REPORTS.GENERATE_ETA_REPORT')}</Title>
      <Form
        form={form}
        layout="vertical"
        name="etaReportForm"
        initialValues={{
          documentType: 'CSV',
        }}
      >
        <FormItem
          name="dateRange"
          label={t('GENERATE_REPORT.TIME_RANGE')}
          rules={[
            {
              required: true,
              message: t('GENERATE_REPORT.PLEASE_PROVIDE_TIME_RANGE'),
            }, {
              validator: timeRangeValidator,
              message: t('GENERATE_REPORT.TIME_RANGE_CAN_BE_MAX_7_DAYS'),
            },
          ]}
        >
          <StyledRangePicker
            showTime={{ format: DEFAULT_TIME }}
            format={DEFAULT_DATE_TIME}
            onChange={setDateRange}
            onOk={setDateRange}
            disabledDate={disabledDate}
            dropdownClassName="ant-picker-shadow-2"
            ranges={{
              'Last 7 days': [moment().subtract(7, 'days'), moment()],
              // eslint-disable-next-line max-len
              'Last week': [moment().subtract(1, 'week').startOf('isoWeek'), moment().subtract(1, 'week').endOf('isoWeek')],
              Yesterday: [moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day')],
            }}

          />
        </FormItem>

        <FormItem
          name="documentType"
          label={t('GENERATE_REPORT.DOCUMENT_TYPE')}
          rules={[
            {
              required: true,
              message: t('GENERATE_REPORT.PLEASE_SELECT_DOCUMENT_TYPE'),
            },
          ]}
        >
          <Radio.Group>
            <Radio value="CSV">CSV</Radio>
          </Radio.Group>
        </FormItem>

        <FormItem>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleGenerateReport}
            loading={loading}
            disabled={isDownloadDisabled}
          >
            {t('COMMON.DOWNLOAD')}
          </Button>
        </FormItem>
      </Form>
    </SectionContainer>
  );
};

export default EtaReportSection;
